
import React, { useState, useEffect, useRef } from 'react';
import { Save, Users, RefreshCw, Shield, Zap, PieChart, Share2, User } from 'lucide-react';
import { calculateFieldPositionName, getZoneColor } from '../utils/fielding';

const PlaymakerModule = () => {
  const [matchFormat, setMatchFormat] = useState('T20');
  const [bowlerType, setBowlerType] = useState('RA Fast');
  const [isRightHandedBat, setIsRightHandedBat] = useState(true);
  const [showWagonWheel, setShowWagonWheel] = useState(false);
  const [showVZone, setShowVZone] = useState(true);

  // Fielders State
  const [fielders, setFielders] = useState([
    { id: 1, x: 50, y: 10, label: 'WK', name: 'Keeper' },
    { id: 2, x: 45, y: 12, label: 'Slip', name: '' },
    { id: 9, x: 50, y: 85, label: 'Bowler', name: 'Bowler' },
  ]);

  const [selectedFielder, setSelectedFielder] = useState(null);
  const fieldRef = useRef(null);

  // -- Event Handlers --

  const handleDragStart = (id) => setSelectedFielder(id);

  const handleDragEnd = () => setSelectedFielder(null);

  const handleMove = (e) => {
    if (!selectedFielder) return;

    // Support Touch and Mouse
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const rect = fieldRef.current.getBoundingClientRect();
    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));

    // Update Position & Dynamic Label
    setFielders(prev => prev.map(f => {
      if (f.id !== selectedFielder) return f;

      const newLabel = calculateFieldPositionName(x, y, isRightHandedBat);
      return { ...f, x, y, label: newLabel };
    }));
  };

  const handleMirror = () => {
    setIsRightHandedBat(!isRightHandedBat);
    setFielders(prev => prev.map(f => ({
      ...f,
      x: 100 - f.x,
      // Re-calculate label for new side
      label: calculateFieldPositionName(100 - f.x, f.y, !isRightHandedBat)
    })));
  };

  return (
    <div className="flex flex-col h-full bg-gray-900 text-white font-sans">

      {/* 1. Top Toolbar */}
      <div className="p-4 bg-gray-800 border-b border-gray-700 flex justify-between items-center shadow-lg z-20">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="text-neon-cyan" size={20} />
            <h1 className="text-lg font-bold tracking-tight text-white">Tactics Board</h1>
          </div>
          <p className="text-[10px] text-gray-400 font-mono uppercase tracking-wider">
            {matchFormat} • {bowlerType} vs {isRightHandedBat ? 'RHB' : 'LHB'}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleMirror}
            className="flex flex-col items-center justify-center p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-all active:scale-95"
          >
            <RefreshCw size={16} className={`mb-1 ${!isRightHandedBat ? 'text-blue-400' : 'text-gray-400'}`} />
            <span className="text-[9px] font-bold">Mirror</span>
          </button>
          <button
            onClick={() => setShowWagonWheel(!showWagonWheel)}
            className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all active:scale-95 ${showWagonWheel ? 'bg-orange-500/20 border border-orange-500/50' : 'bg-gray-700 border border-transparent'}`}
          >
            <PieChart size={16} className={`mb-1 ${showWagonWheel ? 'text-orange-400' : 'text-gray-400'}`} />
            <span className="text-[9px] font-bold">Stats</span>
          </button>
        </div>
      </div>

      {/* 2. Field Editor Canvas */}
      <div className="flex-1 relative overflow-hidden bg-[#2c3e50] flex items-center justify-center">

        {/* Container for Aspect Ratio (Oval) */}
        <div
          ref={fieldRef}
          className="w-[90%] max-w-md aspect-[0.8] relative select-none"
          onMouseMove={handleMove}
          onTouchMove={handleMove}
          onMouseUp={handleDragEnd}
          onTouchEnd={handleDragEnd}
          onMouseLeave={handleDragEnd}
        >
          {/* A. Grass Texture */}
          <div className="absolute inset-0 rounded-[50%] bg-[#4CAF50] shadow-2xl overflow-hidden border-4 border-white/20">
            <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/grass.png')]"></div>

            {/* 30 Yard Circle */}
            <div className="absolute top-[18%] left-[10%] right-[10%] bottom-[18%] rounded-[50%] border-2 border-dashed border-white/30 pointer-events-none"></div>

            {/* Pitch */}
            <div className="absolute top-[40%] left-[45%] w-[10%] h-[20%] bg-[#e0c3a3] rounded-sm shadow-inner pointer-events-none flex flex-col justify-between py-1">
              {/* Stumps */}
              <div className="w-full h-[2px] bg-black/20"></div>
              <div className="w-full h-[2px] bg-black/20"></div>
            </div>

            {/* V-Zone Overlay */}
            {showVZone && (
              <div className="absolute top-[40%] left-1/2 -translate-x-1/2 w-0 h-0 border-l-[120px] border-l-transparent border-r-[120px] border-r-transparent border-t-[180px] border-t-white/5 pointer-events-none filter blur-xl"></div>
            )}
          </div>

          {/* B. Fielders */}
          {fielders.map(f => (
            <div
              key={f.id}
              className="absolute -translate-x-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing z-10 group"
              style={{ left: `${f.x}%`, top: `${f.y}%` }}
              onMouseDown={(e) => handleDragStart(f.id)}
              onTouchStart={(e) => handleDragStart(f.id)}
            >
              {/* Icon */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 transition-transform active:scale-110 
                        ${f.id === selectedFielder ? 'bg-neon-cyan border-white scale-125 z-50' : 'bg-white border-gray-300'}`}>
                {f.name ? <span className="text-[10px] font-bold text-black">{f.name.charAt(0)}</span> : <User size={14} className="text-gray-600" />}
              </div>

              {/* Label (Always visible on selection, otherwise hover) */}
              <div className={`absolute left-1/2 -translate-x-1/2 top-9 px-2 py-1 bg-black/80 backdrop-blur-md rounded text-center min-w-[80px] pointer-events-none transition-opacity
                        ${f.id === selectedFielder ? 'opacity-100 z-50' : 'opacity-0 group-hover:opacity-100'}`}>
                <span className="block text-[10px] text-neon-cyan font-bold uppercase tracking-wider whitespace-nowrap">{f.label}</span>
                {f.name && <span className="block text-[9px] text-gray-300">{f.name}</span>}
              </div>
            </div>
          ))}

          {/* C. Dynamic Label Indicator (Center Screen Overlay) */}
          {selectedFielder && (
            <div className="absolute top-4 left-0 right-0 flex justify-center pointer-events-none animate-fadeIn">
              <div className="bg-black/60 backdrop-blur px-4 py-2 rounded-full border border-neon-cyan/50">
                <span className="text-neon-cyan font-bold text-sm uppercase tracking-widest">
                  {fielders.find(f => f.id === selectedFielder)?.label}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3. Bottom Controls */}
      <div className="p-4 bg-gray-800 border-t border-gray-700 z-20">
        <div className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
          {['Attack', 'Defend', 'Powerplay'].map(preset => (
            <button key={preset} className="px-4 py-2 bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-lg text-xs font-bold whitespace-nowrap transition">
              {preset}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};

export default PlaymakerModule;




